import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { type LucideIcon } from "lucide-react";
import { useSpring } from "framer-motion";
import FlameWrap from "./FlameWrap";

interface FeatureCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  href?: string;
}

export default function FeatureCard({
  icon: Icon,
  title,
  description,
  href,
}: FeatureCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const hoverSpring = useSpring(0, { stiffness: 80, damping: 20 });
  const [flame, setFlame] = useState({
    intensity: 0,
    sparks: 0,
    rim: 0,
    melt: 0,
    distortion: 0,
    ember: 0,
  });

  useEffect(() => {
    hoverSpring.set(isHovered ? 1 : 0);
  }, [isHovered, hoverSpring]);

  useEffect(() => {
    return hoverSpring.on("change", (v) => {
      setFlame({
        intensity: v * 0.8,
        sparks: v * 2.0,
        rim: v * 1.5,
        melt: v * 1.0,
        distortion: v * 4,
        ember: v * 1.5,
      });
    });
  }, [hoverSpring]);

  const cardContent = (
    <FlameWrap
      intensity={flame.intensity}
      height={100}
      spread={10}
      radius={16}
      speed={0.3}
      scale={0.65}
      turbulence={0.6}
      turbulenceScale={0.6}
      turbulenceReach={25}
      sparks={flame.sparks}
      sparkSize={0.4}
      sparkDensity={1.5}
      sparkSpeed={1.5}
      rim={flame.rim}
      melt={flame.melt}
      distortion={flame.distortion}
      ember={flame.ember}
      scorch={0}
      color={[1.0, 0.35, 0.12]}
      wipe={0}
      className="flex flex-col flex-1 h-full w-full"
    >
      <div className="p-8 sm:p-10 flex flex-col h-full w-full relative">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100 z-0 pointer-events-none" />
        
        <div className="relative z-10 flex flex-col flex-1">
          <div className="mb-6 inline-flex p-3 rounded-xl bg-[#0a0603] border border-theme group-hover:border-primary/50 transition-all duration-300 w-fit">
            <Icon className="h-6 w-6 text-[#fdf3ec] group-hover:text-primary transition-colors" />
          </div>
          
          <h3 className="mb-3 font-heading text-xl font-bold text-[#fdf3ec] tracking-tight">
            {title}
          </h3>
          <p className="text-base leading-relaxed text-[#c9a894]">
            {description}
          </p>
        </div>
      </div>
    </FlameWrap>
  );

  const cardClasses = "group relative bg-[#150d08] hover:bg-[#0a0603] rounded-2xl border border-theme transition-all duration-300 flex flex-col h-full hover:border-primary/50 hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] no-underline text-inherit focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/80";

  if (href) {
    return (
      <Link
        to={href}
        className={cardClasses}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div 
      className={cardClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {cardContent}
    </div>
  );
}
